// ── Friet Fighter — gedeelde roster + finetune-config ──
// Single source voor de 9 snack-vechters, gelezen door zowel het dev-lab
// (preview.ts) als de game (character-select + scene). Eén plek om een fighter
// toe te voegen, te hernoemen of zijn per-fighter finetune (schaal/yaw/y-offset)
// te bakken. De finetune-waarden worden in het lab afgesteld (sliders + "copy
// config") en hier ingevuld; nu nog defaults tot ze gekalibreerd zijn.

/** Per-fighter render-correctie bovenop de genormaliseerde GLB. */
export interface Finetune {
  scale: number; // uniforme extra schaal (1 = genormaliseerde hoogte)
  yaw: number; // graden, bovenop de basis-oriëntatie
  yOffset: number; // verticale verschuiving in units (grounding-correctie)
}

export const DEFAULT_FINETUNE: Finetune = { scale: 1, yaw: 0, yOffset: 0 };

export interface FighterDef {
  id: string; // bestandsnaam: public/models/<id>.glb + concepts/<id>-mascot-A.jpeg
  name: string;
  archetype: string;
  glove: string; // hex voor het kleur-stipje / accent
  finetune?: Partial<Finetune>;
}

export const FIGHTERS: FighterDef[] = [
  { id: 'frikandel', name: 'Frikandel', archetype: 'Rushdown / shoto', glove: '#e23b2e' },
  { id: 'kroket', name: 'Kroket', archetype: 'All-rounder', glove: '#3b7fe2' },
  { id: 'bitterbal', name: 'Bitterbal', archetype: 'Grappler', glove: '#46b24a' },
  { id: 'kaassouffle', name: 'Kaassoufflé', archetype: 'Zoner', glove: '#efe4c0' },
  { id: 'berenhap', name: 'Berenhap', archetype: 'Reach / spacing', glove: '#7a4a2a' },
  { id: 'kipcorn', name: 'Kipcorn', archetype: 'Speed / mix-up', glove: '#25c3d6' },
  { id: 'bamiblok', name: 'Bamiblok', archetype: 'Defensive', glove: '#8a4ed6' },
  { id: 'eierbal', name: 'Eierbal', archetype: 'Float / air', glove: '#f06fa0' },
  { id: 'loempia', name: 'Loempia', archetype: 'Technical / counter', glove: '#f08a2e' },
];

export const FIGHTERS_BY_ID: Record<string, FighterDef> = Object.fromEntries(
  FIGHTERS.map((f) => [f.id, f]),
);

/** Boss-def: een vechter buiten de grid (niet in FIGHTERS) met eigen health. */
export interface BossDef extends FighterDef {
  maxHealth: number;
  tint?: number; // optioneel: tint een hergebruikte rig. Patatje Oorlog heeft nu een
  // eigen gerigd model (war-friet) → geen tint meer, z'n echte texture blijft staan.
}

// Patatje Oorlog leeft BUITEN FIGHTERS[] → verschijnt niet in het character-select-grid.
export const BOSSES_BY_ID: Record<string, BossDef> = {
  patatje_oorlog: {
    id: 'patatje_oorlog',
    name: 'Patatje Oorlog',
    archetype: 'Boss / armor',
    glove: '#8a1a0c',
    finetune: { scale: 1.35 },
    maxHealth: 1400,
  },
};

/** Vechter- óf boss-def (boss is niet in FIGHTERS_BY_ID). */
export function combatantById(id: string): FighterDef | BossDef | undefined {
  return FIGHTERS_BY_ID[id] ?? BOSSES_BY_ID[id];
}

const DEFAULT_MAX_HEALTH = 1000; // = sim Tuning.MAX_HEALTH (config kent de sim niet)

/** Max health voor een combatant; caller geeft dit door aan createMatch (boss = hoger). */
export function maxHealthFor(id: string): number {
  return BOSSES_BY_ID[id]?.maxHealth ?? DEFAULT_MAX_HEALTH;
}

/** Volledige finetune voor een fighter of boss (defaults aangevuld). */
export function finetuneFor(id: string): Finetune {
  return { ...DEFAULT_FINETUNE, ...(combatantById(id)?.finetune ?? {}) };
}
